import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useLingui } from "@lingui/react/macro";
import { ChevronDown, Image as ImageIcon, Info, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { OVERLAY_NESTED } from "@/components/ui/overlay";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCreateMaterial } from "@/hooks/useMaterials";
import { materialApi } from "@/lib/api/services/material.api";
import { supplierApi } from "@/lib/api/services/supplier.api";
import { companyApi } from "@/lib/api/services/company.api";
import { getApiErrorMessage, isConflictError } from "@/lib/api/api";
import {
  converterPayload,
  converterProblem,
  converterStateFromConversion,
  emptyConverterState,
  hasConverterInput,
  legacyPackFor,
  MAX_YIELD_PCT,
  type ConverterState,
} from "@/lib/materials/conversion-grid";
import { formatPackSize } from "@/lib/materials/pack-size";
import { PackSizeConverter } from "@/components/materials/pack-size/PackSizeConverter";
import { Skeleton } from "@/components/ui/skeleton";
import { useUoms, usePackContainers } from "@/hooks/useUomReference";
import { useMaterialConversions } from "@/hooks/useMaterialConversions";
import { UnitSelect } from "@/components/units/UnitSelect";
import { SupplierSelect } from "@/components/common/entity-select/SupplierSelect";
import { useUnits } from "@/hooks/useUnits";
import { FileUploader } from "@/components/common/FileUploader";
import type { UploadedFileItem } from "@/components/common/FileUploader";
import type { Allergen, CreateMaterialInput, Material, UpdateMaterialInput } from "@/types/material";
import type { Uom } from "@/types/material-conversion";
import type { UnitFamily, UnitFamilyGroup } from "@/types/unit";

const DEFAULT_ALLERGENS = [
  "Gluten",
  "Egg",
  "Dairy",
  "Soy",
  "Peanut",
  "Tree Nut",
  "Fish",
  "Shellfish",
  "Sesame",
  "Wheat",
  "Mustard",
  "Celery",
];

type MaterialType = "RAW" | "COMPOUND" | "READY_TO_USE";

/** Same debounce as RoleSheet: long enough to batch a burst of typing into one PATCH. */
const AUTOSAVE_DELAY_MS = 300;

const ALLERGEN_LABEL_TO_CODE: Record<string, string> = {
  Gluten: "GLUTEN",
  Egg: "EGG",
  Dairy: "DAIRY",
  Soy: "SOY",
  Peanut: "PEANUT",
  "Tree Nut": "TREE_NUT",
  Fish: "FISH",
  Shellfish: "SHELLFISH",
  Sesame: "SESAME",
  Wheat: "WHEAT",
  Mustard: "MUSTARD",
  Celery: "CELERY",
};

/**
 * Stand-ins for the seeded built-ins, used only until `GET /materials/allergens`
 * answers. Without them the picker would flash empty on every open.
 */
const FALLBACK_ALLERGENS: Allergen[] = DEFAULT_ALLERGENS.map((name, index) => ({
  id: -(index + 1),
  name,
  code: ALLERGEN_LABEL_TO_CODE[name] ?? name.toUpperCase(),
  organizationId: null,
}));

/** The reference table's type, as the family `UnitSelect` groups by. */
const UOM_TYPE_FAMILY: Record<Uom["type"], UnitFamily> = { WEIGHT: "weight", LIQUID: "volume", EACH: "count" };
/** The reference table's code, where the costing table (and so a stored unit) spells it differently. */
const UOM_CODE_UNIT: Record<string, string> = { ea: "each", dz: "dozen" };

/**
 * Every unit of the `uoms` reference table, grouped for `UnitSelect`: the whole
 * table, not the tenant's shortlist or one family. Units are stored by the
 * costing table's names, so a count is "each", not "ea".
 */
function uomGroupsFor(uoms: Uom[]): UnitFamilyGroup[] {
  const groups: UnitFamilyGroup[] = [];
  for (const uom of uoms) {
    const family = UOM_TYPE_FAMILY[uom.type];
    const unit = UOM_CODE_UNIT[uom.code] ?? uom.code;
    const group = groups.find((g) => g.family === family);
    if (!group) groups.push({ family, units: [unit] });
    else if (!group.units.includes(unit)) group.units.push(unit);
  }
  return groups;
}

interface FormState {
  name: string;
  type: MaterialType;
  unitOfMeasure: string;
  recipeUom: string;
  /** How much of `packUom` one `unitOfMeasure` holds: 1 case = 85 each. */
  packQuantity: string;
  packUom: string;
  prepDishStation: string;
  prepNotes: string;
  sku: string;
  description: string;
  caloriesPerUnit: string;
  proteinG: string;
  carbsG: string;
  fatG: string;
  fiberG: string;
}

const EMPTY: FormState = {
  name: "",
  type: "RAW",
  unitOfMeasure: "",
  recipeUom: "",
  packQuantity: "",
  packUom: "",
  prepDishStation: "",
  prepNotes: "",
  sku: "",
  description: "",
  caloriesPerUnit: "",
  proteinG: "",
  carbsG: "",
  fatG: "",
  fiberG: "",
};

interface CreateMaterialDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (materialName: string) => void;
  editMaterial?: Material;
}

export function CreateMaterialDialog({ open, onOpenChange, onCreated, editMaterial }: CreateMaterialDialogProps) {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const createMaterial = useCreateMaterial();

  function showReadableError(message: string) {
    const duration = Math.min(20000, Math.max(7000, message.length * 70));
    toast.error(message, { duration });
  }

  const [form, setForm] = useState<FormState>(EMPTY);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFileItem[]>([]);
  const [isExistingImageDetached, setIsExistingImageDetached] = useState(false);
  // Selection is held by code, not label: a custom allergen's name is the user's
  // own text and only the code is meaningful to the API.
  const [selectedAllergenCodes, setSelectedAllergenCodes] = useState<string[]>([]);
  const [customAllergenName, setCustomAllergenName] = useState<string | null>(null);
  // Null means no supplier picked yet; distinct from "" which would be a
  // supplier the user is in the middle of naming (see `newSupplierName`).
  const [supplierId, setSupplierId] = useState<number | null>(null);
  const [newSupplierName, setNewSupplierName] = useState<string | null>(null);
  // Null is a real answer here — an ingredient with no concept is shared across
  // the whole organization, which is what every ingredient used to be.
  const [companyId, setCompanyId] = useState<number | null>(null);
  const [isSubRecipeIngredient, setIsSubRecipeIngredient] = useState(false);
  // The pack size and what a case holds, saved as the ingredient's supplier
  // item once the ingredient itself is saved.
  const [packConverter, setPackConverter] = useState<ConverterState>(emptyConverterState);
  const [packConverterSeededFor, setPackConverterSeededFor] = useState<number | null>(null);
  const [isSavingPackSize, setIsSavingPackSize] = useState(false);
  // Autosave (T-1410): the serialised material fields last saved (or loaded),
  // and the last attempt that failed, so a failure is shown once and kept.
  const [autosavedKey, setAutosavedKey] = useState<string | null>(null);
  const [autosaveError, setAutosaveError] = useState<{ key: string; message: string } | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isEditing = editMaterial !== undefined;

  const updateMaterial = useMutation({
    mutationFn: (data: UpdateMaterialInput) => materialApi.update(editMaterial!.id, data),
    onSuccess: async (_material, data) => {
      await savePackSize(editMaterial!.id, data.supplierId ?? null);
      queryClient.invalidateQueries({ queryKey: ["materials"] });
      toast.success(t`Material updated`);
      onCreated?.(form.name.trim());
      handleClose();
    },
    onError: (error: unknown, data) => {
      const message = getApiErrorMessage(error, t`Failed to update material`);
      // Held as an autosave failure too, so autosave does not resend it.
      setAutosaveError({ key: JSON.stringify(materialFields(data.supplierId ?? null)), message });
      showReadableError(message);
    },
  });

  // The key is the PATCH body, serialised, so what is compared is exactly what
  // was sent.
  const autosaveMaterial = useMutation({
    mutationFn: ({ id, key }: { id: number; key: string }) =>
      materialApi.update(id, JSON.parse(key) as UpdateMaterialInput),
    onSuccess: (_material, { key }) => {
      setAutosavedKey(key);
      queryClient.invalidateQueries({ queryKey: ["materials"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "materials"] });
    },
    onError: (error: unknown, { key }) => {
      // The typed value stays in the field; only the save is reported.
      const message = getApiErrorMessage(error, t`Failed to update material`);
      setAutosaveError({ key, message });
      showReadableError(message);
    },
  });

  // The picker has to include allergens this tenant added earlier, so it comes
  // from the API rather than the hardcoded list.
  const { data: fetchedAllergens } = useQuery({
    queryKey: ["allergens"],
    queryFn: () => materialApi.getAllergens(),
    enabled: open,
  });
  const allergenOptions = fetchedAllergens?.length ? fetchedAllergens : FALLBACK_ALLERGENS;

  // The whole catalog: the purchase-unit list needs the container units
  // (case, box, ...) whatever the kitchen has switched off for recipe lines.
  const { groups: unitGroups } = useUnits(open, "all");
  const { data: uoms } = useUoms(undefined, open);
  // Both unit pickers offer every unit of the UoM reference table (any UOM is
  // selectable). A purchase unit adds the containers, which are not measures;
  // a recipe unit never is one. Until the table loads they fall back to /units.
  const recipeUnitGroups = useMemo(() => (uoms ? uomGroupsFor(uoms) : unitGroups), [uoms, unitGroups]);
  const purchaseUnitGroups = useMemo(
    () => (uoms ? [...recipeUnitGroups, ...unitGroups.filter((group) => group.family === "container")] : unitGroups),
    [uoms, recipeUnitGroups, unitGroups],
  );
  const { data: packContainers } = usePackContainers(open);
  const { data: editConversions } = useMaterialConversions(editMaterial?.id, open && isEditing);
  // An ingredient bought from several suppliers has several supplier items;
  // the dialog edits the most recently updated one.
  const primaryConversion = editConversions?.[0] ?? null;
  /**
   * Bought by the case (or bag, or box), what a case holds is also the
   * ingredient's own pack size — "1 case = 85 each" — which costing uses to
   * turn a price per case into a price per gram when no supplier item is
   * priced (T-501). Bought loose, by a measure, there is no case to describe.
   */
  const purchaseIsContainer = unitGroups.some(
    (group) => group.family === "container" && group.units.includes(form.unitOfMeasure.trim()),
  );
  const derivedPack =
    purchaseIsContainer && uoms && packContainers ? legacyPackFor(packConverter, uoms, packContainers) : null;
  // Moving an ingredient off a container (case → kg) leaves its old pack
  // pair saying "1 kg = 85 each", which costing would believe. It is cleared.
  const wasContainer =
    isEditing && unitGroups.some((group) => group.family === "container" && group.units.includes(editMaterial.unitOfMeasure));
  const dropsLegacyPack = wasContainer && !purchaseIsContainer;
  // What the list will show: the converter's own, else the pack pair already
  // stored on the ingredient, so the line agrees with the list for either.
  const storedPackQuantity = parseFloat(form.packQuantity);
  const previewPack =
    derivedPack ??
    (!dropsLegacyPack && storedPackQuantity > 0 && form.packUom.trim() !== ""
      ? { packQuantity: storedPackQuantity, packUom: form.packUom.trim() }
      : null);

  const { data: companies } = useQuery({
    queryKey: ["companies", "material-concepts"],
    queryFn: () => companyApi.getAll(),
    enabled: open,
  });
  const conceptOptions = useMemo(
    () => (companies ?? []).filter((company) => company.isActive),
    [companies],
  );
  /**
   * A single-concept operator should never have to answer a question with one
   * possible answer, so the field pre-fills. Editing is left alone: an existing
   * ingredient that is shared across concepts stays shared unless it is changed.
   */
  const soleConceptId = conceptOptions.length === 1 ? (conceptOptions[0]?.id ?? null) : null;
  const effectiveCompanyId = companyId ?? (isEditing ? null : soleConceptId);
  const isConceptRequired = conceptOptions.length > 1;

  const createAllergen = useMutation({
    mutationFn: (name: string) => materialApi.createAllergen(name),
    onSuccess: (allergen) => {
      queryClient.invalidateQueries({ queryKey: ["allergens"] });
      // Naming one is how you pick it — the chip is selected straight away, and
      // it is on the list for the next ingredient because the API stored it.
      setSelectedAllergenCodes((current) =>
        current.includes(allergen.code) ? current : [...current, allergen.code],
      );
      setCustomAllergenName(null);
    },
    onError: (error: unknown) => {
      showReadableError(getApiErrorMessage(error, t`Failed to add allergen`));
    },
  });

  const createSupplier = useMutation({
    mutationFn: (name: string) => supplierApi.create({ name }),
    onSuccess: (supplier) => {
      queryClient.invalidateQueries({ queryKey: ["entity-select", "suppliers"] });
      setSupplierId(supplier.id);
      setNewSupplierName(null);
    },
    onError: (error: unknown) => {
      showReadableError(getApiErrorMessage(error, t`Failed to add supplier`));
    },
  });

  const [prevOpen, setPrevOpen] = useState(open);
  const [prevEditId, setPrevEditId] = useState(editMaterial?.id);
  if (prevOpen !== open || prevEditId !== editMaterial?.id) {
    setPrevOpen(open);
    setPrevEditId(editMaterial?.id);
    setAutosavedKey(null);
    setAutosaveError(null);
    if (open && editMaterial) {
      setForm({
        name: editMaterial.name,
        type: editMaterial.type,
        unitOfMeasure: editMaterial.unitOfMeasure,
        recipeUom: editMaterial.recipeUom ?? "",
        packQuantity: editMaterial.packQuantity != null ? String(editMaterial.packQuantity) : "",
        packUom: editMaterial.packUom ?? "",
        prepDishStation: editMaterial.prepDishStation ?? "",
        prepNotes: editMaterial.prepNotes ?? "",
        sku: editMaterial.sku ?? "",
        description: editMaterial.description ?? "",
        caloriesPerUnit: editMaterial.caloriesPerUnit != null ? String(editMaterial.caloriesPerUnit) : "",
        proteinG: editMaterial.proteinG != null ? String(editMaterial.proteinG) : "",
        carbsG: editMaterial.carbsG != null ? String(editMaterial.carbsG) : "",
        fatG: editMaterial.fatG != null ? String(editMaterial.fatG) : "",
        fiberG: editMaterial.fiberG != null ? String(editMaterial.fiberG) : "",
      });
      setUploadedFiles([]);
      setIsExistingImageDetached(false);
      const existingAllergenCodes = (editMaterial.allergens ?? [])
        .map((item) => item.allergen?.code)
        .filter((code): code is string => Boolean(code));
      setSelectedAllergenCodes(existingAllergenCodes);
      setCustomAllergenName(null);
      setSupplierId(editMaterial.supplierId ?? null);
      setNewSupplierName(null);
      setCompanyId(editMaterial.companyId ?? null);
      setIsSubRecipeIngredient(Boolean(editMaterial.isSubRecipeIngredient));
    } else if (!open) {
      setPackConverter(emptyConverterState());
      setPackConverterSeededFor(null);
      setForm(EMPTY);
      setUploadedFiles([]);
      setIsExistingImageDetached(false);
      setSelectedAllergenCodes([]);
      setCustomAllergenName(null);
      setCompanyId(null);
      setIsSubRecipeIngredient(false);
    }
  }

  if (open && editMaterial && editConversions && uoms && packContainers && packConverterSeededFor !== editMaterial.id) {
    setPackConverterSeededFor(editMaterial.id);
    setPackConverter(
      primaryConversion ? converterStateFromConversion(primaryConversion, uoms, packContainers) : emptyConverterState(),
    );
  }

  /**
   * Saves the pack size as the ingredient's supplier item, after the
   * ingredient itself. A failure here does not undo the ingredient; the cook is
   * told, and can finish it on the ingredient's "UoM & pack size" tab.
   */
  async function savePackSize(materialId: number, materialSupplierId: number | null) {
    if (!uoms || !hasConverterInput(packConverter)) return;
    // A supplier item keeps the supplier it already has (set on the UoM tab);
    // only one with none takes the ingredient's. Overwriting it wiped that
    // choice, and failed outright on a shared-catalogue ingredient (T-1404).
    const { supplierId: conversionSupplierId, ...sizes } = converterPayload(packConverter, uoms);
    const payload = conversionSupplierId != null ? sizes : { ...sizes, supplierId: materialSupplierId };
    setIsSavingPackSize(true);
    try {
      if (primaryConversion) await materialApi.updateConversion(materialId, primaryConversion.id, payload);
      else await materialApi.createConversion(materialId, payload);
      // The list and Overview price from the conversion chart, so they refresh too.
      queryClient.invalidateQueries({ queryKey: ["materials"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "materials"] });
    } catch (error) {
      const reason = getApiErrorMessage(error, t`Failed to save`);
      showReadableError(t`The ingredient was saved, but its pack size was not: ${reason}`);
    } finally {
      setIsSavingPackSize(false);
    }
  }

  /**
   * "Other" used to append a chip labelled "Custom 1" that was never persisted
   * and never sent, so a named allergen was impossible. A name is required here
   * for that reason — an unnamed allergen tells the next cook nothing.
   */
  function saveCustomAllergen() {
    const name = (customAllergenName ?? "").trim();
    if (name === "") {
      toast.error(t`Please name the custom allergen`);
      return;
    }
    createAllergen.mutate(name);
  }

  function saveNewSupplier() {
    const name = (newSupplierName ?? "").trim();
    if (name === "") {
      toast.error(t`Please name the supplier`);
      return;
    }
    createSupplier.mutate(name);
  }

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  /**
   * A supplier typed into "+ Add new supplier" but never added used to be
   * dropped without a word (T-1404). It is created now, and the save carries
   * it. Resolves to the supplier to save, or `false` when it could not be
   * created — the error is already on screen, so the save stops there.
   */
  async function resolveSupplierId(): Promise<number | null | false> {
    const name = (newSupplierName ?? "").trim();
    if (name === "") return supplierId;
    try {
      return (await createSupplier.mutateAsync(name)).id;
    } catch {
      return false;
    }
  }

  function handleClose() {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = null;
    setAutosavedKey(null);
    setAutosaveError(null);
    setPackConverter(emptyConverterState());
    setPackConverterSeededFor(null);
    setForm(EMPTY);
    setUploadedFiles([]);
    setIsExistingImageDetached(false);
    setSelectedAllergenCodes([]);
    setCustomAllergenName(null);
    setSupplierId(null);
    setNewSupplierName(null);
    setCompanyId(null);
    setIsSubRecipeIngredient(false);
    onOpenChange(false);
  }

  const hasReplacementUpload = uploadedFiles.length > 0;
  const shouldKeepExistingImage = isEditing && !isExistingImageDetached && !hasReplacementUpload;
  const selectedImageUrl = uploadedFiles[0]?.imageUrl ?? (shouldKeepExistingImage ? editMaterial?.imageUrl : undefined);
  const selectedThumbnailUrl =
    uploadedFiles[0]?.thumbnailUrl ?? (shouldKeepExistingImage ? editMaterial?.thumbnailUrl : undefined);

  /**
   * PATCH /materials/:id skips any field that arrives as `undefined`, so
   * detaching a photo has to send an explicit `null` — otherwise the removal is
   * accepted, toasted as saved, and silently discarded (T-506).
   */
  const clearsExistingImage = isEditing && isExistingImageDetached && !hasReplacementUpload;
  const patchedImageUrl = clearsExistingImage ? null : selectedImageUrl;
  const patchedThumbnailUrl = clearsExistingImage ? null : selectedThumbnailUrl;

  // Description is its own field, not Prep notes. Emptying one that was saved
  // sends "" because the API ignores an `undefined` rather than clearing it.
  const normalizedDescription = form.description.trim() || (editMaterial?.description ? "" : undefined);

  /**
   * The ingredient's own fields: what autosave sends in edit mode (T-1410).
   * The pack pair is left out — it follows the pack-size converter, which keeps
   * its explicit Save — except that moving off a container clears it.
   */
  function materialFields(resolvedSupplierId: number | null): UpdateMaterialInput {
    return {
      companyId: effectiveCompanyId,
      name: form.name.trim(),
      type: form.type,
      unitOfMeasure: form.unitOfMeasure.trim(),
      recipeUom: form.recipeUom.trim() || null,
      ...(dropsLegacyPack ? { packQuantity: null, packUom: null } : {}),
      imageUrl: patchedImageUrl,
      thumbnailUrl: patchedThumbnailUrl,
      sku: form.sku.trim() || undefined,
      supplierId: resolvedSupplierId,
      description: normalizedDescription,
      prepDishStation: form.prepDishStation.trim() || undefined,
      prepNotes: form.prepNotes.trim() || undefined,
      allergenCodes: selectedAllergenCodes,
      isSubRecipeIngredient,
      caloriesPerUnit: form.caloriesPerUnit !== "" ? parseFloat(form.caloriesPerUnit) : undefined,
      proteinG: form.proteinG !== "" ? parseFloat(form.proteinG) : undefined,
      carbsG: form.carbsG !== "" ? parseFloat(form.carbsG) : undefined,
      fatG: form.fatG !== "" ? parseFloat(form.fatG) : undefined,
      fiberG: form.fiberG !== "" ? parseFloat(form.fiberG) : undefined,
    };
  }

  function materialFieldsProblem(): string | null {
    if (!form.name.trim()) return t`Please enter an ingredient name`;
    if (!form.unitOfMeasure.trim()) return t`Please select a unit of measure`;
    // With more than one concept there is no answer to guess, and an ingredient
    // filed under the wrong one is worse than one that was never saved.
    if (isConceptRequired && effectiveCompanyId == null) return t`Please choose a concept for this ingredient`;
    return null;
  }

  // Autosave, following RoleSheet: a 300 ms debounce over the latest values, so
  // a burst of typing is one PATCH.
  const autosaveKey = isEditing ? JSON.stringify(materialFields(supplierId)) : null;
  // The fields as loaded are the baseline. Taken once the open/close
  // adjustment above has settled, so it is the seeded form, not the empty one.
  if (open && isEditing && autosavedKey === null && prevOpen === open && prevEditId === editMaterial?.id) {
    setAutosavedKey(autosaveKey);
  }
  const autosaveProblem = materialFieldsProblem();
  const hasUnsavedChanges = autosaveKey !== null && autosavedKey !== null && autosaveKey !== autosavedKey;
  const autosaveFailed = hasUnsavedChanges && autosaveError?.key === autosaveKey;
  const isAutosavingThis = autosaveMaterial.isPending && autosaveMaterial.variables?.key === autosaveKey;
  // An explicit Save in flight sends these fields itself.
  const isExplicitSaving = updateMaterial.isPending || isSavingPackSize;
  const shouldAutosave =
    open && hasUnsavedChanges && !autosaveFailed && autosaveProblem === null && !isAutosavingThis && !isExplicitSaving;
  const autosaveId = editMaterial?.id;
  const { mutate: autosave } = autosaveMaterial;

  useEffect(() => {
    if (!shouldAutosave || autosaveKey === null || autosaveId === undefined) return;
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      autosave({ id: autosaveId, key: autosaveKey });
    }, AUTOSAVE_DELAY_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = null;
    };
  }, [shouldAutosave, autosaveKey, autosaveId, autosave]);

  /**
   * Saves whatever autosave has not got to yet, so closing never loses an
   * edit. Resolves `false` — and the dialog stays open, the value kept — when
   * that save fails; a failure already shown does not hold the dialog twice.
   */
  async function flushAutosave(): Promise<boolean> {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = null;
    if (!editMaterial) return true;
    const hasPendingSupplier = (newSupplierName ?? "").trim() !== "";
    if ((!hasUnsavedChanges && !hasPendingSupplier) || autosaveFailed || autosaveKey === null) return true;
    if (autosaveProblem) {
      setAutosaveError({ key: autosaveKey, message: autosaveProblem });
      toast.error(autosaveProblem);
      return false;
    }
    const resolvedSupplierId = await resolveSupplierId();
    if (resolvedSupplierId === false) return false;
    const key = JSON.stringify(materialFields(resolvedSupplierId));
    if (key === autosavedKey || (autosaveMaterial.isPending && autosaveMaterial.variables?.key === key)) return true;
    try {
      await autosaveMaterial.mutateAsync({ id: editMaterial.id, key });
      return true;
    } catch {
      return false;
    }
  }

  /** Every way out of the dialog but a completed Save comes through here. */
  async function requestClose() {
    if (isEditing && !(await flushAutosave())) return;
    handleClose();
  }

  async function handleSave() {
    const fieldsProblem = materialFieldsProblem();
    if (fieldsProblem) {
      toast.error(fieldsProblem);
      return;
    }
    // The API rejects half a pack size, so catch it here rather than let the
    // save fail with a message written for an API client.
    const hasPackQuantity = form.packQuantity.trim() !== "";
    const hasPackUom = form.packUom.trim() !== "";
    if (hasPackQuantity !== hasPackUom) {
      toast.error(t`A pack size needs both a count and a unit — e.g. 85 each`);
      return;
    }
    if (hasPackQuantity && !(parseFloat(form.packQuantity) > 0)) {
      toast.error(t`Pack quantity must be a number greater than 0`);
      return;
    }
    const packProblem = hasConverterInput(packConverter) ? converterProblem(packConverter) : null;
    if (packProblem === "EMPTY") {
      toast.error(t`Say what a case holds — a count, a weight or a volume — or clear the pack size`);
      return;
    }
    if (packProblem === "YIELD") {
      toast.error(t`Yield has to be between 1% and ${MAX_YIELD_PCT}%.`);
      return;
    }
    if (packProblem === "PRICE") {
      toast.error(t`Case price has to be a number.`);
      return;
    }

    // In edit mode Save sends the whole ingredient, pack pair included, in
    // place of any autosave still waiting, then the pack size, then closes.
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = null;
    const resolvedSupplierId = await resolveSupplierId();
    if (resolvedSupplierId === false) return;

    if (isEditing) {
      const data: UpdateMaterialInput = {
        ...materialFields(resolvedSupplierId),
        packQuantity: dropsLegacyPack
          ? null
          : (derivedPack?.packQuantity ?? (hasPackQuantity ? parseFloat(form.packQuantity) : null)),
        packUom: dropsLegacyPack ? null : (derivedPack?.packUom ?? (hasPackUom ? form.packUom.trim() : null)),
      };
      updateMaterial.mutate(data);
      return;
    }

    const data: CreateMaterialInput = {
      companyId: effectiveCompanyId ?? undefined,
      name: form.name.trim(),
      type: form.type,
      unitOfMeasure: form.unitOfMeasure.trim(),
      recipeUom: form.recipeUom.trim() || undefined,
      packQuantity: derivedPack?.packQuantity ?? (hasPackQuantity ? parseFloat(form.packQuantity) : undefined),
      packUom: derivedPack?.packUom ?? (hasPackUom ? form.packUom.trim() : undefined),
      imageUrl: selectedImageUrl,
      thumbnailUrl: selectedThumbnailUrl,
      sku: form.sku.trim() || undefined,
      supplierId: resolvedSupplierId ?? undefined,
      description: normalizedDescription,
      prepDishStation: form.prepDishStation.trim() || undefined,
      prepNotes: form.prepNotes.trim() || undefined,
      allergenCodes: selectedAllergenCodes,
      isSubRecipeIngredient,
      caloriesPerUnit: form.caloriesPerUnit !== "" ? parseFloat(form.caloriesPerUnit) : undefined,
      proteinG: form.proteinG !== "" ? parseFloat(form.proteinG) : undefined,
      carbsG: form.carbsG !== "" ? parseFloat(form.carbsG) : undefined,
      fatG: form.fatG !== "" ? parseFloat(form.fatG) : undefined,
      fiberG: form.fiberG !== "" ? parseFloat(form.fiberG) : undefined,
    };

    createMaterial.mutate(data, {
      onSuccess: async (created) => {
        await savePackSize(created.id, resolvedSupplierId);
        toast.success(t`Ingredient added`);
        onCreated?.(form.name.trim());
        handleClose();
      },
      onError: (error: unknown) => {
        const name = form.name.trim();
        showReadableError(
          isConflictError(error)
            ? t`An ingredient named "${name}" already exists in your organization`
            : getApiErrorMessage(error, t`Failed to add ingredient`),
        );
      },
    });
  }

  const isPending = createMaterial.isPending || updateMaterial.isPending || isSavingPackSize || createSupplier.isPending;
  const materialIdValue = isEditing ? `ING-${String(editMaterial.id).padStart(3, "0")}` : "";

  const autosaveStatus = !isEditing
    ? null
    : autosaveMaterial.isPending
      ? t`Saving…`
      : autosaveFailed && autosaveError
        ? autosaveError.message
        : hasUnsavedChanges && autosaveProblem
          ? autosaveProblem
          : hasUnsavedChanges
            ? t`Saving…`
            : t`All changes saved`;
  const isAutosaveStatusError = !autosaveMaterial.isPending && (autosaveFailed || (hasUnsavedChanges && autosaveProblem !== null));

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          void requestClose();
        }
      }}
    >
      {/* Opens on top of AddIngredientDialog, hence the raised scrim. */}
      <DialogContent
        hideClose
        overlayClassName={OVERLAY_NESTED}
        className="z-[60] flex w-[56.25rem] max-w-[95vw] max-h-[92vh] flex-col gap-0 overflow-hidden rounded-xl border-(--color-border) bg-(--color-surface-content) p-0 shadow-2xl"
      >
          <DialogTitle className="sr-only">
            {isEditing ? t`Edit ingredient` : t`Add ingredient`}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {isEditing
              ? t`Update ingredient details and save your changes.`
              : t`Enter ingredient details and add a new ingredient.`}
          </DialogDescription>

          <div className="flex items-start justify-between border-b border-(--color-border) px-5 py-4">
            <div className="flex flex-col gap-1">
              <h2 className="text-[32px] leading-8 font-medium text-(--color-text-primary)">
                {isEditing ? t`Edit ingredient` : t`Add ingredient`}
              </h2>
              <p className="text-sm text-(--color-text-secondary)">
                {t({
                  id: "ingredient.modal.description.header",
                  message: "Capture ingredient details, allergens, and cost data for accurate recipe calculations.",
                })}
              </p>
            </div>
            <Button
              variant="ghost"
              type="button"
              onClick={() => void requestClose()}
              className="size-8 rounded-full p-0 text-(--color-text-secondary) hover:bg-(--color-surface-muted) hover:text-(--color-text-primary)"
            >
              <X className="size-4" />
            </Button>
          </div>

          <div className="overflow-y-auto">
            <SectionRow
              title={t`Ingredient details`}
              description={t({
                id: "ingredient.modal.description.details",
                message: "Define the ingredient name and system-generated identifier.",
              })}
            >
              <div className="grid grid-cols-2 gap-3">
                <FormField label={t`Ingredient name`} required>
                  <input
                    aria-label={t`Ingredient name`}
                    type="text"
                    value={form.name}
                    onChange={(event) => set("name", event.target.value)}
                    placeholder={t`Enter ingredient name...`}
                    className="h-10 w-full rounded-lg border border-(--color-border) bg-(--color-surface-content) px-3 text-sm text-(--color-text-primary) outline-none placeholder:text-(--color-text-secondary) focus:border-(--color-ring)"
                  />
                </FormField>
                <FormField label={t`Supplier`}>
                  {newSupplierName === null ? (
                    <SupplierSelect
                      value={supplierId}
                      onChange={(id) => setSupplierId(id)}
                      onClear={() => setSupplierId(null)}
                      placeholder={t`Select a supplier...`}
                    />
                  ) : (
                    <div className="flex items-center gap-2">
                      <input
                        aria-label={t`New supplier name`}
                        type="text"
                        autoFocus
                        value={newSupplierName}
                        onChange={(event) => setNewSupplierName(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            event.preventDefault();
                            saveNewSupplier();
                          }
                          if (event.key === "Escape") setNewSupplierName(null);
                        }}
                        placeholder={t`E.g. Sysco`}
                        className="h-10 w-full rounded-lg border border-(--color-border) bg-(--color-surface-content) px-3 text-sm text-(--color-text-primary) outline-none placeholder:text-(--color-text-secondary) focus:border-(--color-ring)"
                      />
                      <Button
                        type="button"
                        variant="brand-outline"
                        size="sm"
                        onClick={saveNewSupplier}
                        disabled={createSupplier.isPending}
                      >
                        {t`Add`}
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        aria-label={t`Cancel adding a supplier`}
                        onClick={() => setNewSupplierName(null)}
                      >
                        <X className="size-4" />
                      </Button>
                    </div>
                  )}
                  {newSupplierName === null && (
                    <button
                      type="button"
                      onClick={() => setNewSupplierName("")}
                      className="mt-1 self-start text-xs font-medium text-(--color-text-secondary) hover:text-(--color-text-primary)"
                    >
                      {t`+ Add new supplier`}
                    </button>
                  )}
                </FormField>

                <FormField label={t`Concept`} required={isConceptRequired} optional={!isConceptRequired}>
                  <div className="relative">
                    <select
                      aria-label={t`Concept`}
                      value={effectiveCompanyId == null ? "" : String(effectiveCompanyId)}
                      onChange={(event) =>
                        setCompanyId(event.target.value === "" ? null : Number(event.target.value))
                      }
                      className="h-10 w-full appearance-none rounded-lg border border-(--color-border) bg-(--color-surface-content) px-3 pr-10 text-sm text-(--color-text-primary) outline-none focus:border-(--color-ring)"
                    >
                      {/* Shared is a legitimate choice, not an empty one — until a
                          second concept exists, at which point one has to be picked. */}
                      <option value="">
                        {isConceptRequired ? t`Select a concept...` : t`Shared across concepts`}
                      </option>
                      {conceptOptions.map((company) => (
                        <option key={company.id} value={String(company.id)}>
                          {company.name}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-(--color-text-secondary)" />
                  </div>
                </FormField>
                <FormField label={t`Ingredient ID`}>
                  <input
                    aria-label={t`Ingredient ID`}
                    disabled
                    type="text"
                    value={materialIdValue}
                    placeholder={t`Data will be filled automatically`}
                    className="h-10 w-full rounded-lg border border-(--color-border) bg-(--color-surface-muted) px-3 text-sm text-(--color-text-secondary) outline-none placeholder:text-(--color-text-secondary)/80"
                  />
                </FormField>
              </div>
            </SectionRow>

            <SectionRow
              title={t`Ingredient proportions`}
              description={t({
                // New id: the old one's catalog text still mentions the
                // Quantity box this section no longer has (T-1408).
                id: "ingredient.modal.description.units",
                message: "Set the unit it is bought in and the unit recipes use.",
              })}
            >
              <div className="grid grid-cols-2 gap-3">
                <FormField label={t`Unit of Measure`} required>
                  <UnitSelect
                    label={t`Unit of Measure`}
                    value={form.unitOfMeasure}
                    onChange={(value) => set("unitOfMeasure", value)}
                    groups={purchaseUnitGroups}
                  />
                </FormField>
                <FormField label={t`Recipe unit`}>
                  <UnitSelect
                    label={t`Recipe unit`}
                    value={form.recipeUom}
                    onChange={(value) => set("recipeUom", value)}
                    groups={recipeUnitGroups}
                    // Left empty, the API uses the pack unit, else the purchase unit
                    // (resolveRecipeUom); say so rather than promise the purchase unit.
                    placeholder={t`Pack unit, else purchase unit`}
                  />
                </FormField>
              </div>
            </SectionRow>

            {/* Full width, unlike the other sections: the converter is a grid
                of three unit types that does not fit beside a label column. */}
            <div className="flex flex-col gap-4 border-b border-(--color-border) px-5 py-4">
              <div className="flex flex-col gap-1.5">
                <p className="text-base font-medium text-(--color-text-primary)">{t`Pack size and recipe units`}</p>
                <p className="text-sm leading-[1.2] text-(--color-text-secondary)">
                  {t({
                    id: "ingredient.modal.description.packConverter",
                    message:
                      "Enter the pack size as the invoice shows it, then what a case holds by count, weight or volume, and the unit recipes use for each.",
                  })}
                </p>
                {editConversions && editConversions.length > 1 ? (
                  <p className="text-sm text-(--color-text-secondary)">
                    {t`This ingredient has ${editConversions.length} supplier items. This edits the most recent one; the others are on its UoM & pack size tab.`}
                  </p>
                ) : null}
              </div>
              {uoms && packContainers && (!isEditing || editConversions) ? (
                <PackSizeConverter
                  value={packConverter}
                  onChange={setPackConverter}
                  uoms={uoms}
                  containers={packContainers}
                  showSupplier={false}
                />
              ) : (
                <Skeleton className="h-64 w-full" />
              )}
              {previewPack ? (
                <p className="text-sm text-(--color-text-secondary)">
                  {t`Shows in the ingredient list as ${formatPackSize({ unitOfMeasure: form.unitOfMeasure.trim(), ...previewPack })}`}
                </p>
              ) : null}
            </div>

            <SectionRow
              title={t`Allergens`}
              description={t({
                id: "ingredient.modal.description.allergens",
                message: "Select known allergens to support food safety and labeling.",
              })}
            >
              <div className="flex flex-wrap gap-2">
                {allergenOptions.map((allergen) => {
                  const isSelected = selectedAllergenCodes.includes(allergen.code);
                  return (
                    <button
                      key={allergen.code}
                      type="button"
                      onClick={() => {
                        setSelectedAllergenCodes((current) => {
                          if (current.includes(allergen.code)) {
                            return current.filter((item) => item !== allergen.code);
                          }
                          return [...current, allergen.code];
                        });
                      }}
                      className={`flex h-9 items-center gap-1.5 rounded-[10px] border px-2.5 text-sm transition-colors ${
                        isSelected
                          ? "border-(--color-action) bg-(--color-surface-content) text-(--color-text-primary)"
                          : "border-(--color-border) bg-(--color-surface-muted) text-(--color-text-primary)"
                      }`}
                    >
                      <span
                        className={`size-4 rounded-full border ${
                          isSelected ? "border-(--color-action) bg-(--color-action)" : "border-(--color-border-strong) bg-(--color-surface-content)"
                        }`}
                      />
                      {allergen.name}
                    </button>
                  );
                })}
                {customAllergenName === null ? (
                  <button
                    type="button"
                    onClick={() => setCustomAllergenName("")}
                    className="flex h-9 items-center gap-1.5 rounded-[10px] border border-dashed border-(--color-border-strong) bg-(--color-surface-content) px-2.5 text-sm text-(--color-text-primary)"
                  >
                    <Plus className="size-4" />
                    {t`Add custom allergen`}
                  </button>
                ) : null}
              </div>
              {customAllergenName !== null ? (
                <div className="mt-3 flex items-start gap-2">
                  <FormField label={t`Custom allergen name`} required>
                    <input
                      aria-label={t`Custom allergen name`}
                      type="text"
                      autoFocus
                      value={customAllergenName}
                      onChange={(event) => setCustomAllergenName(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          saveCustomAllergen();
                        }
                        if (event.key === "Escape") setCustomAllergenName(null);
                      }}
                      placeholder={t`E.g. Sesame paste`}
                      className="h-10 w-full rounded-lg border border-(--color-border) bg-(--color-surface-content) px-3 text-sm text-(--color-text-primary) outline-none placeholder:text-(--color-text-secondary) focus:border-(--color-ring)"
                    />
                  </FormField>
                  <div className="flex items-center gap-2 pt-6">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={saveCustomAllergen}
                      disabled={createAllergen.isPending}
                    >
                      {t`Add allergen`}
                    </Button>
                    {/* The dialog footer also has a Cancel; name this one so it
                        is distinguishable to a screen reader. */}
                    <Button
                      type="button"
                      variant="ghost"
                      aria-label={t`Cancel adding an allergen`}
                      onClick={() => setCustomAllergenName(null)}
                    >
                      {t`Cancel`}
                    </Button>
                  </div>
                </div>
              ) : null}
            </SectionRow>

            <SectionRow
              title={t`More details`}
              description={t({
                id: "ingredient.modal.description.moreDetails",
                message: "Add prep context, notes, and an optional image.",
              })}
            >
              <div className="flex flex-col gap-3">
                <FormField label={t`Prep dish / station`} optional>
                  <div className="relative">
                    <input
                      aria-label={t`Prep dish / station`}
                      type="text"
                      value={form.prepDishStation}
                      onChange={(event) => set("prepDishStation", event.target.value)}
                      placeholder={t`Select`}
                      className="h-10 w-full rounded-lg border border-(--color-border) bg-(--color-surface-content) px-3 pr-10 text-sm text-(--color-text-primary) outline-none placeholder:text-(--color-text-secondary) focus:border-(--color-ring)"
                    />
                    <ChevronDown className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-(--color-text-secondary)" />
                  </div>
                </FormField>

                <FormField label={t`Description`} optional>
                  <textarea
                    aria-label={t`Description`}
                    value={form.description}
                    onChange={(event) => set("description", event.target.value)}
                    rows={3}
                    placeholder={t`What this ingredient is, e.g. variety, grade or brand`}
                    className="resize-none rounded-lg border border-(--color-border) bg-(--color-surface-content) px-3 py-2.5 text-sm text-(--color-text-primary) outline-none placeholder:text-(--color-text-secondary) focus:border-(--color-ring)"
                  />
                </FormField>

                <FormField label={t`Prep notes`} optional>
                  <textarea
                    aria-label={t`Prep notes`}
                    value={form.prepNotes}
                    onChange={(event) => set("prepNotes", event.target.value)}
                    rows={4}
                    placeholder={t`Select`}
                    className="resize-none rounded-lg border border-(--color-border) bg-(--color-surface-content) px-3 py-2.5 text-sm text-(--color-text-primary) outline-none placeholder:text-(--color-text-secondary) focus:border-(--color-ring)"
                  />
                </FormField>

                <FormField label={t`Image`} optional>
                  <div className="flex items-start gap-4">
                    <div className="flex size-16 shrink-0 items-center justify-center rounded-xl border border-dashed border-(--color-border-strong) bg-(--color-surface-content) text-(--color-text-secondary)">
                      <ImageIcon className="size-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <FileUploader
                        key={`${open ? "open" : "closed"}-${editMaterial?.id ?? "new"}`}
                        module="material"
                        label={undefined}
                        multiple={false}
                        maxFiles={1}
                        disabled={isPending}
                        onUploadedItemsChange={setUploadedFiles}
                        existingImage={
                          isEditing
                            ? {
                                imageUrl: editMaterial?.imageUrl,
                                thumbnailUrl: editMaterial?.thumbnailUrl,
                                label: t`Current thumbnail`,
                              }
                            : undefined
                        }
                        onExistingImageDetachedChange={setIsExistingImageDetached}
                      />
                    </div>
                  </div>
                </FormField>
              </div>
            </SectionRow>

            <SectionRow
              title={t`Sub-recipe ingredients`}
              description={t({
                id: "ingredient.modal.description.subRecipe",
                message: "Mark when this ingredient is made from other ingredients.",
              })}
              borderless
            >
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  role="switch"
                  aria-checked={isSubRecipeIngredient}
                  onClick={() => setIsSubRecipeIngredient((value) => !value)}
                  className={`relative h-6 w-11 rounded-full transition-colors ${
                    isSubRecipeIngredient ? "bg-(--color-action)" : "bg-(--color-surface-muted)"
                  }`}
                >
                  <span
                    className={`absolute top-1/2 size-5 -translate-y-1/2 rounded-full bg-white transition-all ${
                      isSubRecipeIngredient ? "left-[22px]" : "left-[2px]"
                    }`}
                  />
                </button>
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-1.5 text-sm font-medium text-(--color-text-primary)">
                    {t`Sub-recipe ingredients`}
                    <Info className="size-4 text-(--color-text-secondary)" />
                  </div>
                  <p className="text-sm text-(--color-text-secondary)">{t`This ingredient contains other ingredients.`}</p>
                </div>
              </div>
            </SectionRow>
          </div>

          <div className="flex items-center justify-end gap-3 border-t border-(--color-border) bg-(--color-surface-content) px-5 py-4">
            {autosaveStatus !== null ? (
              <p
                aria-live="polite"
                className={`mr-auto text-sm ${isAutosaveStatusError ? "text-(--color-danger-text)" : "text-(--color-text-secondary)"}`}
              >
                {autosaveStatus}
              </p>
            ) : null}
            {/* Edits already save as they are made, so in edit mode this only closes. */}
            <Button
              variant="brand-outline"
              type="button"
              onClick={() => void requestClose()}
              className="h-13 w-[200px] text-base"
            >
              {isEditing ? t`Close` : t`Cancel`}
            </Button>
            <Button
              type="button"
              onClick={() => void handleSave()}
              disabled={isPending || !form.name.trim() || !form.unitOfMeasure.trim()}
              className="h-13 w-[200px] rounded-[10px] bg-(--color-action) text-base font-medium text-(--color-action-fg) transition-colors hover:bg-(--color-action)/90 disabled:opacity-50"
            >
              {isPending
                ? isEditing
                  ? t`Saving...`
                  : t`Creating...`
                : isEditing
                  ? t`Save changes`
                  : t`Save ingredient`}
            </Button>
          </div>
      </DialogContent>
    </Dialog>
  );
}

function SectionRow({
  title,
  description,
  children,
  borderless = false,
}: {
  title: string;
  description: string;
  children: ReactNode;
  borderless?: boolean;
}) {
  return (
    <div className={`grid grid-cols-[300px_1fr] ${borderless ? "" : "border-b border-(--color-border)"}`}>
      <div className="bg-(--color-surface-muted) px-5 py-4">
        <div className="flex flex-col gap-1.5">
          <p className="text-base font-medium text-(--color-text-primary)">{title}</p>
          <p className="text-sm leading-[1.2] text-(--color-text-secondary)">{description}</p>
        </div>
      </div>
      <div className="px-5 py-4">{children}</div>
    </div>
  );
}

function FormField({
  label,
  required,
  optional,
  helper,
  children,
}: {
  label: string;
  required?: boolean;
  optional?: boolean;
  helper?: string;
  children: ReactNode;
}) {
  const { t } = useLingui();
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-medium text-(--color-text-primary)">
        {label}
        {required && <span className="ml-0.5 text-(--color-danger-text)">*</span>}
        {optional && <span className="ml-1 font-normal text-(--color-text-secondary)">{t`(optional)`}</span>}
        {helper && <span className="ml-1 font-normal text-(--color-text-secondary)">{helper}</span>}
      </label>
      {children}
    </div>
  );
}
